'use client';

import { useRef, useState, useCallback } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Play, Pause, ArrowRight } from 'lucide-react';
import { WaveformBars } from './waveform-bars';
import { trackEvent } from '@/lib/analytics';

type VoiceId = 'a' | 'b';

const AUDIO_SOURCES: Record<VoiceId, string> = {
  a: '/audio/demo-real.mp3',
  b: '/audio/demo-clone.mp3',
};

/** Voice A is real, Voice B is the AI clone */
const REAL_VOICE: VoiceId = 'a';

const RESULT_MESSAGES: Record<'correct' | 'wrong', string> = {
  correct: "You're sharp. But would mom catch it at 2 AM, half asleep?",
  wrong: "Neither could 70% of people. Your mom at 2 AM has no chance. (McAfee, 2023)",
};

export function VoiceChallenge() {
  const shouldReduceMotion = useReducedMotion();
  const audioRefA = useRef<HTMLAudioElement>(null);
  const audioRefB = useRef<HTMLAudioElement>(null);

  const [playing, setPlaying] = useState<VoiceId | null>(null);
  const [selected, setSelected] = useState<VoiceId | null>(null);
  const [revealed, setRevealed] = useState(false);

  const audioRefs: Record<VoiceId, React.RefObject<HTMLAudioElement | null>> = {
    a: audioRefA,
    b: audioRefB,
  };

  const pauseAll = useCallback(() => {
    audioRefA.current?.pause();
    audioRefB.current?.pause();
    setPlaying(null);
  }, []);

  const togglePlay = useCallback(
    (voice: VoiceId) => {
      if (revealed) return;
      const current = audioRefs[voice].current;
      const other = audioRefs[voice === 'a' ? 'b' : 'a'].current;
      if (playing === voice) {
        current?.pause();
        setPlaying(null);
        return;
      }
      other?.pause();
      current?.play();
      setPlaying(voice);
    },
    [playing, revealed, audioRefs],
  );

  const handlePick = useCallback(
    (voice: VoiceId) => {
      if (revealed) return;
      pauseAll();
      setSelected(voice);
      setRevealed(true);
    },
    [revealed, pauseAll],
  );

  const isCorrect = selected === REAL_VOICE;
  const resultMessage = selected !== null ? RESULT_MESSAGES[isCorrect ? 'correct' : 'wrong'] : '';
  const ariaResult =
    selected !== null
      ? `Voice ${selected === REAL_VOICE ? 'B' : 'A'} was the AI clone. 70% of people cannot tell the difference.`
      : '';

  return (
    <div style={{ position: 'relative' }}>
      {/* Premium backdrop aurora glow behind Voice Challenge */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          height: '90%',
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, transparent 75%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          padding: 32,
          borderRadius: 24,
          background: '#07160E',
          border: '1px solid rgba(34, 197, 94, 0.15)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        {/* Hidden audio elements */}
        <audio ref={audioRefA} src={AUDIO_SOURCES.a} preload="metadata" />
        <audio ref={audioRefB} src={AUDIO_SOURCES.b} preload="metadata" />

        {/* Title */}
        <p
          style={{
            fontFamily: "'Clash Display', sans-serif",
            fontSize: '1.375rem',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.75)',
            textAlign: 'center',
            marginBottom: 24,
            letterSpacing: '0.2px',
          }}
        >
          Try it. Now imagine mom at 2 AM.
        </p>

        {/* Voice cards — click card to pick */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 8 }}>
          {(['a', 'b'] as const).map((voice) => {
            const isPicked = selected === voice;
            const isDimmed = revealed && selected !== voice;

            return (
              <button
                type="button"
                key={voice}
                onClick={() => handlePick(voice)}
                disabled={revealed}
                aria-pressed={selected === voice}
                aria-label={`Pick Voice ${voice.toUpperCase()} as the real voice`}
                style={{
                  padding: '26px 18px',
                  borderRadius: 18,
                  background: isPicked 
                    ? 'rgba(34, 197, 94, 0.12)' 
                    : 'rgba(255, 255, 255, 0.04)',
                  border: `1.5px solid ${
                    isPicked 
                      ? '#22C55E' 
                      : 'rgba(255, 255, 255, 0.08)'
                  }`,
                  textAlign: 'center',
                  cursor: revealed ? 'default' : 'pointer',
                  transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  gap: 12,
                  opacity: isDimmed ? 0.25 : 1,
                  transform: isDimmed ? 'scale(0.96)' : undefined,
                  pointerEvents: isDimmed ? 'none' as const : undefined,
                  boxShadow: isPicked ? '0 0 20px rgba(34, 197, 94, 0.15)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!revealed && !isPicked) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.4)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 16px 36px rgba(0,0,0,0.4), 0 0 15px rgba(34,197,94,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!revealed && !isPicked) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }
                }}
              >
                {/* Label */}
                <span
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: isPicked ? '#22C55E' : 'rgba(255,255,255,0.4)',
                    letterSpacing: 2,
                    textTransform: 'uppercase' as const,
                    transition: 'color 0.2s',
                  }}
                >
                  Voice {voice.toUpperCase()}
                </span>

                {/* Play button */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); togglePlay(voice); }}
                  disabled={revealed}
                  aria-label={playing === voice ? `Pause Voice ${voice.toUpperCase()}` : `Play Voice ${voice.toUpperCase()}`}
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: '50%',
                    border: `1.5px solid ${playing === voice ? '#22C55E' : 'rgba(255,255,255,0.18)'}`,
                    background: playing === voice ? '#22C55E' : 'transparent',
                    color: playing === voice ? '#052E16' : 'white',
                    cursor: revealed ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s',
                    fontSize: '1.25rem',
                    boxShadow: playing === voice ? '0 0 24px rgba(34,197,94,0.45)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!revealed && playing !== voice) {
                      e.currentTarget.style.borderColor = '#22C55E';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!revealed && playing !== voice) {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
                      e.currentTarget.style.transform = '';
                    }
                  }}
                >
                  {playing === voice ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" style={{ marginLeft: 3 }} />}
                </button>

                {/* Waveform */}
                <WaveformBars 
                  bars={7} 
                  height={44} 
                  active={playing === voice} 
                  color={playing === voice ? '#22C55E' : 'rgba(255, 255, 255, 0.15)'} 
                />

                {/* Hint */}
                {!revealed && (
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', transition: 'all 0.2s' }}>
                    Tap to pick this one
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Accessible live region */}
        <div aria-live="polite" className="sr-only">{ariaResult}</div>

        {/* Reveal */}
        <AnimatePresence>
          {revealed && selected !== null && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={
                isCorrect
                  ? { opacity: 1, y: 0 }
                  : { opacity: 1, y: 0, x: shouldReduceMotion ? 0 : [0, -8, 8, -4, 4, 0] }
              }
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{ textAlign: 'center', paddingTop: 20 }}
            >
              <span
                style={{
                  display: 'inline-block',
                  borderRadius: 999,
                  padding: '5px 16px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'uppercase' as const,
                  letterSpacing: 1.2,
                  background: isCorrect ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  border: `1.5px solid ${isCorrect ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  color: isCorrect ? '#4ADE80' : '#F87171',
                  boxShadow: isCorrect ? '0 0 16px rgba(34,197,94,0.25)' : '0 0 16px rgba(239,68,68,0.25)',
                }}
              >
                {isCorrect ? 'Correct' : 'Wrong'}
              </span>
              <p style={{ fontSize: '1rem', color: '#C4C0BB', lineHeight: 1.6, marginTop: 12 }}>
                {resultMessage}
              </p>

              <Link
                href="/dashboard/simulate"
                onClick={() =>
                  trackEvent('landing_cta_clicked', {
                    ctaLabel: 'voice_challenge_post_reveal',
                    destination: '/dashboard/simulate',
                  })
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 20,
                  padding: '14px 26px',
                  borderRadius: 999,
                  background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                  color: '#0A1F12',
                  fontSize: '0.9375rem',
                  fontWeight: 800,
                  textDecoration: 'none',
                  boxShadow: '0 10px 24px rgba(34,197,94,0.3)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(34,197,94,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 10px 24px rgba(34,197,94,0.3)'; }}
              >
                Send mom this exact call
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
